import { Box, Button, Typography } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { foldFailureReason, isFoldInFlight } from "@xop/dashboard-shared";

import { PageLoader } from "@/components/ui/page-loader";

import { useCan } from "../../../auth/permissions.js";
import { salesApi } from "../client.js";
import { SalesEmptyState, SalesErrorState } from "../components/SalesEmptyState.js";
import { describeAgentError } from "../describeAgentError.js";
import { devLog } from "../devLog.js";
import { HarvesterStrategy } from "./HarvesterStrategy.js";
import { StrategyMarkdown } from "./StrategyMarkdown.js";
import { StrategyVersionPicker } from "./StrategyVersionPicker.js";

/**
 * Tab 3 — Strategy.
 *
 * The agent's strategist writes a markdown memo every Monday (and
 * on-demand when a steering note arrives) describing:
 *
 *   - what XOP sells (auto-summarised from xopnetworks.com + the KB)
 *   - which verticals/geographies look hot this period
 *   - what was tried, what worked, what didn't
 *   - the agent's planned focus for the week
 *
 * The operator reads this like a memo from a smart analyst. The
 * version picker sits as a horizontal chip strip *above* the memo so
 * the history is the first thing on screen — clicking a chip swaps
 * the body in place.
 */
export function StrategyPage(): JSX.Element {
  useEffect(() => devLog("strategy/mount"), []);

  const canEdit = useCan("sales:edit");
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);

  // Which agent is running — the Strategy tab is mode-aware: in CONTACTS mode
  // it shows the harvester's strategy (`<HarvesterStrategy />` below), not
  // the lead strategist's memo. Declared before the regen query so `enabled`
  // below can read it.
  const ctl = useQuery({
    queryKey: ["sales", "agent-control"],
    queryFn: salesApi.getAgentControl,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
  const inContactsMode = ctl.data?.mode === "contacts";

  const current = useQuery({
    queryKey: ["sales", "strategy", activeId ?? "current"],
    queryFn: () =>
      activeId ? salesApi.getStrategyVersion(activeId) : salesApi.getStrategy(),
    refetchInterval: activeId ? false : 5 * 60_000,
  });

  const history = useQuery({
    queryKey: ["sales", "strategy-history"],
    queryFn: salesApi.getStrategyHistory,
    refetchInterval: 5 * 60_000,
  });

  // "Refresh now" queues the rewrite rather than holding the request open
  // (2026-08-27), so the progress and any failure live here instead of in the
  // button's disabled state.
  const regen = useQuery({
    queryKey: ["sales", "strategy-regen"],
    queryFn: salesApi.getStrategyRegen,
    refetchInterval: (query) => (isFoldInFlight(query.state.data) ? 5_000 : 30_000),
    refetchIntervalInBackground: false,
    // In CONTACTS mode this tab renders `<HarvesterStrategy />` (below) and
    // this query's banner can never appear — polling it anyway just hammers
    // /v2/strategy/regen for a mode that will never read the response. Gated
    // on `ctl.data` being loaded (not just "not contacts") so a fresh mount
    // never fires the FIRST fetch before the mode is even known — the mode
    // read is itself the source of truth here, so waiting one tick for it
    // beats guessing.
    enabled: ctl.data !== undefined && !inContactsMode,
  });

  // A completed fold must make the NEW memo appear — otherwise the banner
  // above just vanishes and the body under it keeps showing the old version
  // for up to the next 5-minute /strategy poll. `refresh.onSuccess` fires at
  // t=0, while the job is still queued, so it refetches the OLD memo; this is
  // the invalidation that has to happen once the job actually lands. Keyed on
  // the job id so it fires exactly once per completed job, not on every poll
  // that still sees the same `done` job.
  const lastHandledFoldId = useRef<string | null>(null);
  useEffect(() => {
    const job = regen.data;
    if (!job || job.state !== "done") return;
    if (lastHandledFoldId.current === job.id) return;
    lastHandledFoldId.current = job.id;
    qc.invalidateQueries({ queryKey: ["sales", "strategy"] });
    qc.invalidateQueries({ queryKey: ["sales", "strategy-history"] });
  }, [regen.data, qc]);

  const [refreshError, setRefreshError] = useState<string | null>(null);

  const refresh = useMutation({
    mutationFn: () => salesApi.refreshStrategy(),
    onMutate: () => setRefreshError(null),
    onSuccess: () => {
      setRefreshError(null);
      qc.invalidateQueries({ queryKey: ["sales", "strategy"] });
      qc.invalidateQueries({ queryKey: ["sales", "strategy-history"] });
      qc.invalidateQueries({ queryKey: ["sales", "strategy-regen"] });
    },
    // The regen used to be inline, so a failure here meant the rewrite
    // itself had failed — that copy now belongs to the `regen.data.state ===
    // "failed"` banner below. Post-change the ONLY way this mutation can
    // fail is the fast enqueue never landing, so silence here would be the
    // one broken link in the chain: the operator presses the button and
    // nothing on screen ever says whether it worked.
    onError: (err) => {
      setRefreshError(describeAgentError(err, "something went wrong"));
    },
  });

  if (inContactsMode) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        <HarvesterStrategy />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box component="header" sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: 1.5 }}>
        <Typography component="h2" sx={{ fontSize: "1.25rem", fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" }}>
          Strategy memo
        </Typography>
        {canEdit && (
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            sx={{ fontSize: 14, fontWeight: 500, color: "text.primary", px: 1.5, py: 0.75, "&:hover": { bgcolor: "surfaceSoft" } }}
          >
            {refresh.isPending ? "Refreshing…" : "Refresh now"}
          </Button>
        )}
      </Box>

      {refreshError && (
        <Typography role="alert" sx={{ fontSize: 12, color: "warning.main" }}>
          Couldn&apos;t ask for a new memo — {refreshError}. Nothing was
          queued — press Refresh now to try again.
        </Typography>
      )}

      {isFoldInFlight(regen.data) && (
        <Typography role="status" sx={{ fontSize: 12, color: "text.secondary" }}>
          Writing a new memo…
        </Typography>
      )}

      {regen.data?.state === "failed" && (
        <Typography role="alert" sx={{ fontSize: 12, color: "warning.main" }}>
          Couldn&apos;t write a new memo — {foldFailureReason(regen.data.error)}. The
          memo below is the last one that succeeded.
        </Typography>
      )}

      <StrategyVersionPicker
        versions={history.data?.versions ?? []}
        active={current.data ?? null}
        onPick={(id) => {
          // Clicking the latest chip drops the override and resumes
          // polling /strategy (the implicit "current" path).
          if (history.data && history.data.versions[0]?.id === id) {
            setActiveId(null);
          } else {
            setActiveId(id);
          }
        }}
      />

      {current.isLoading && <PageLoader />}

      {current.isError && (
        <SalesErrorState error={current.error} surface="strategy" />
      )}

      {current.data === null && (
        <SalesEmptyState
          variant="info"
          title="No strategy memo yet"
          description="The strategist writes the first memo after the company-self-knowledge bootstrap finishes. Refresh in a few minutes — or click 'Refresh now' to nudge it."
        />
      )}

      {current.data && (
        <Box
          component="article"
          sx={{
            display: "grid",
            gap: 1.5,
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            boxShadow: (t: Theme) => t.shadows[1],
            p: 3,
          }}
        >
          <Typography sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
            v{current.data.version} · written {new Date(current.data.created_at).toLocaleString()}
          </Typography>
          <StrategyMarkdown source={current.data.body_markdown} />
        </Box>
      )}
    </Box>
  );
}
